// ─────────────────────────────────────────────────────────────
// Avatar utilities — shared across all components
// Centralised to avoid the same functions being copy-pasted in
// DiscussionCard, DiscussionDetailPage, ProfilePage,
// SettingsPage, UserHoverCard, MentionTextarea, TopNav
// ─────────────────────────────────────────────────────────────

// stripHtml and contentPreview are canonical in @ekkam/shared.
// Re-exported here so all component imports (`from '../lib/avatar'`)
// continue to work without change.
export { stripHtml, contentPreview, decodeEntities } from '@ekkam/shared'

const AVATAR_COLORS = [
  '#7c3aed', // purple  (default)
  '#06b6d4', // cyan
  '#f97316', // orange
  '#22c55e', // green
  '#ec4899', // pink
  '#eab308', // yellow
]

/**
 * Returns 1–2 uppercase initials for a display name.
 * "Edwin Thomas" → "ET", "Alice" → "A"
 */
export const getInitials = (name: string): string =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)

/**
 * Returns a deterministic accent colour for a name.
 * Same name always gets the same colour.
 */
export const getAvatarColor = (name: string): string =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length] ?? AVATAR_COLORS[0]!

/**
 * Formats a date string as a relative time label.
 * Used in profile headers and hover cards.
 */
export const timeAgoLong = (date: string): string => {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (diff < 86400) return 'today'
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`
  if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))}mo ago`
  return `${Math.floor(diff / (86400 * 365))}y ago`
}

/**
 * Formats a date string as a short relative time label.
 * Used inside comment cards.
 */
export const timeAgoShort = (date: string): string => {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (diff < 60) return `${diff}s`
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  return `${Math.floor(diff / 86400)}d`
}
